import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Avatar,
  Container,
  Grid,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  CircularProgress,
  Alert,
  Tabs,
  Tab,
  Divider,
  LinearProgress,
  Tooltip,
} from '@mui/material';
import {
  School,
  Logout,
  Person,
  Assessment,
  Timeline,
  TrendingUp,
  Visibility,
  Refresh,
  Search,
  FilterList,
  Download,
  Analytics,
  PlayArrow,
  Stop,
  Schedule,
  CheckCircle,
  Cancel,
  Warning,
  RestartAlt,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { RootState, AppDispatch } from '../../store';
import IntegrityPanel from '../proctoring/IntegrityPanel';
import LiveScheduler from '../live/LiveScheduler';
import { logoutUser } from '../../store/slices/authSlice';
import { brandColors } from '../../theme';
import learningService, { LearningSession } from '../../services/learningService';

// Types for learning activities
interface StudentActivity {
  id: string;
  sessionId: string;
  questionId: string;
  questionIndex: number;
  startTime: string;
  endTime?: string;
  totalTimeTaken: number;
  answerIndex?: number;
  answerText?: string;
  result?: boolean;
  isMarkedForReview: boolean;
  status: 'NOT_STARTED' | 'IN_PROGRESS' | 'ANSWERED' | 'REVIEWED' | 'SKIPPED';
}

interface StudentPerformance {
  totalSessions: number;
  completedSessions: number;
  averageScore: number;
  totalQuestionsAnswered: number;
  correctAnswers: number;
  accuracy: number;
  totalTimeSpent: number;
  subjects: string[];
  recentActivity: LearningSession[];
}

// Mock data - replace with actual API calls
const mockSessions: LearningSession[] = [
  {
    id: '1',
    studentId: 'student123',
    studentName: 'Aarav Sharma',
    studentPhone: '+91-9876543210',
    packageId: 'pkg1',
    packageName: 'Class-04 Science Mock Test - 01',
    subject: 'Science',
    grade: 'Class-04',
    series: 'Mock Test',
    status: 'COMPLETED',
    score: 85,
    totalScore: 100,
    currentQuestionIndex: 20,
    totalQuestions: 20,
    answeredQuestions: 20,
    timeLeft: 0,
    startTime: '2024-01-15T10:00:00.000+00:00',
    endTime: '2024-01-15T11:30:00Z',
    lastActivityTime: '2024-01-15T11:30:00Z',
    openingDate: '2024-01-10T00:00:00Z',
    closingDate: '2024-01-20T23:59:59Z',
  },
  {
    id: '2',
    studentId: 'student123',
    studentName: 'Aarav Sharma',
    studentPhone: '+91-9876543210',
    packageId: 'pkg2',
    packageName: 'Class-04 Mathematics Mock Test - 02',
    subject: 'Mathematics',
    grade: 'Class-04',
    series: 'Mock Test',
    status: 'IN_PROGRESS',
    score: 45,
    totalScore: 100,
    currentQuestionIndex: 12,
    totalQuestions: 25,
    answeredQuestions: 12,
    timeLeft: 1800,
    startTime: '2024-01-16T14:00:00.000+00:00',
    lastActivityTime: '2024-01-16T15:30:00Z',
    openingDate: '2024-01-15T00:00:00Z',
    closingDate: '2024-01-25T23:59:59Z',
  },
];

const mockPerformance: StudentPerformance = {
  totalSessions: 15,
  completedSessions: 12,
  averageScore: 78.5,
  totalQuestionsAnswered: 450,
  correctAnswers: 354,
  accuracy: 78.7,
  totalTimeSpent: 18000, // in minutes
  subjects: ['Science', 'Mathematics', 'English'],
  recentActivity: mockSessions,
};

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`simple-tabpanel-${index}`}
      aria-labelledby={`simple-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ p: 3 }}>{children}</Box>}
    </div>
  );
}

const WelcomePage: React.FC = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch<AppDispatch>();
  const { user } = useSelector((state: RootState) => state.auth);

  const [tabValue, setTabValue] = useState(0);
  const [sessions, setSessions] = useState<LearningSession[]>([]);
  const [performance, setPerformance] = useState<StudentPerformance | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [selectedSession, setSelectedSession] = useState<LearningSession | null>(null);
  const [sessionDetailsOpen, setSessionDetailsOpen] = useState(false);
  const [resetConfirmOpen, setResetConfirmOpen] = useState(false);
  const [sessionToReset, setSessionToReset] = useState<LearningSession | null>(null);
  const [isResetting, setIsResetting] = useState(false);

  useEffect(() => {
    const fetchSessions = async () => {
      setLoading(true);
      setError(null);
      try {
        const { sessions } = await learningService.getAllSessions();
        setSessions(sessions);
        // Optionally, calculate performance summary here if needed
      } catch (err: any) {
        setError(err.message || 'Failed to fetch sessions');
      } finally {
        setLoading(false);
      }
    };
    fetchSessions();
  }, []);

  const handleLogout = async () => {
    await dispatch(logoutUser());
    navigate('/login');
  };

  const getInitials = (name: string) => {
    return name
      .split(' ')
      .map(word => word.charAt(0))
      .join('')
      .toUpperCase()
      .slice(0, 2);
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'COMPLETED':
        return 'success';
      case 'IN_PROGRESS':
        return 'warning';
      case 'ABANDONED':
        return 'error';
      case 'EXPIRED':
        return 'default';
      default:
        return 'default';
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'COMPLETED':
        return <CheckCircle />;
      case 'IN_PROGRESS':
        return <PlayArrow />;
      case 'ABANDONED':
        return <Cancel />;
      case 'EXPIRED':
        return <Schedule />;
      default:
        return <Warning />;
    }
  };

  const formatTime = (minutes: number) => {
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    return `${hours}h ${mins}m`;
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const filteredSessions = sessions.filter(session => {
    const matchesSearch = (session.packageName?.toLowerCase() || '').includes(searchTerm.toLowerCase()) ||
                         (session.subject?.toLowerCase() || '').includes(searchTerm.toLowerCase());
    const matchesStatus = statusFilter === 'all' || session.status === statusFilter;
    const hasQuestions = session.totalQuestions > 0; // Filter out sessions with 0 total questions
    return matchesSearch && matchesStatus && hasQuestions;
  });

  const handleSessionClick = (session: LearningSession) => {
    setSelectedSession(session);
    setSessionDetailsOpen(true);
  };

  const handleResetClick = (session: LearningSession) => {
    setSessionToReset(session);
    setResetConfirmOpen(true);
  };

  const handleResetConfirm = async () => {
    if (!sessionToReset) return;

    setIsResetting(true);
    try {
      const success = await learningService.resetSession(sessionToReset.id);
      if (success) {
        // Refresh the sessions list to show updated data
        const updatedSessions = await learningService.getAllSessions();
        setSessions(updatedSessions.sessions);
        setError(null);

        // Show success message (you could add a snackbar here)
        console.log('Session reset successfully');
      } else {
        setError('Failed to reset session');
      }
    } catch (error) {
      console.error('Error resetting session:', error);
      setError('Failed to reset session');
    } finally {
      setIsResetting(false);
      setResetConfirmOpen(false);
      setSessionToReset(null);
    }
  };

  const handleResetCancel = () => {
    setResetConfirmOpen(false);
    setSessionToReset(null);
  };

  const handleRefresh = () => {
    setLoading(true);
    // Simulate API call
    setTimeout(() => {
      setLoading(false);
    }, 1000);
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        background: `linear-gradient(135deg, ${brandColors.primary} 0%, ${brandColors.secondary} 50%, ${brandColors.accent} 100%)`,
        p: 2,
      }}
    >
      <Container maxWidth="xl">
        {/* Header */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <School
              sx={{
                fontSize: 48,
                color: 'white',
                mr: 2,
              }}
            />
            <Typography variant="h4" component="h1" sx={{ fontWeight: 'bold', color: 'white' }}>
              Learning Activity Management
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Avatar
              sx={{
                width: 40,
                height: 40,
                bgcolor: 'white',
                color: brandColors.primary,
                fontSize: '1rem',
              }}
            >
              {user?.name ? getInitials(user.name) : <Person />}
            </Avatar>
            <Typography variant="body2" sx={{ color: 'white' }}>
              {user?.name || 'User'}
            </Typography>
            <Button
              variant="outlined"
              startIcon={<Logout />}
              onClick={handleLogout}
              sx={{
                color: 'white',
                borderColor: 'white',
                '&:hover': {
                  borderColor: 'white',
                  bgcolor: 'rgba(255, 255, 255, 0.1)',
                },
              }}
            >
              Logout
            </Button>
          </Box>
        </Box>

        {/* Performance Overview Cards */}
        {/* Optionally, hide or show based on performance state */}
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', md: 'repeat(4, 1fr)' }, gap: 3, mb: 3 }}>
          <Card sx={{ bgcolor: 'rgba(255, 255, 255, 0.9)', backdropFilter: 'blur(10px)' }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Box>
                  <Typography color="textSecondary" gutterBottom>
                    Total Sessions
                  </Typography>
                  <Typography variant="h4" component="div">
                    {sessions.length}
                  </Typography>
                </Box>
                <Assessment sx={{ fontSize: 40, color: brandColors.primary }} />
              </Box>
            </CardContent>
          </Card>

          <Card sx={{ bgcolor: 'rgba(255, 255, 255, 0.9)', backdropFilter: 'blur(10px)' }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Box>
                  <Typography color="textSecondary" gutterBottom>
                    Accuracy
                  </Typography>
                  <Typography variant="h4" component="div">
                    {performance?.accuracy || 0}%
                  </Typography>
                </Box>
                <CheckCircle sx={{ fontSize: 40, color: 'success.main' }} />
              </Box>
            </CardContent>
          </Card>
          <Card sx={{ bgcolor: 'rgba(255, 255, 255, 0.9)', backdropFilter: 'blur(10px)' }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Box>
                  <Typography color="textSecondary" gutterBottom>
                    Time Spent
                  </Typography>
                  <Typography variant="h4" component="div">
                    {performance?.totalTimeSpent ? formatTime(performance.totalTimeSpent) : 'N/A'}
                  </Typography>
                </Box>
                <Timeline sx={{ fontSize: 40, color: brandColors.accent }} />
              </Box>
            </CardContent>
          </Card>
        </Box>

        {/* Main Content */}
        <Card sx={{ bgcolor: 'rgba(255, 255, 255, 0.95)', backdropFilter: 'blur(10px)' }}>
          <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
            <Tabs value={tabValue} onChange={(e, newValue) => setTabValue(newValue)}>
              <Tab label="Active Sessions" />
              <Tab label="Performance Analytics" />
              <Tab label="Subject Overview" />
              <Tab label="Live Reviews" />
            </Tabs>
          </Box>

          <TabPanel value={tabValue} index={0}>
            {/* Active Sessions Tab */}
            <Box sx={{ mb: 3 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Typography variant="h6">Learning Sessions</Typography>
                <Box sx={{ display: 'flex', gap: 2 }}>
                  <TextField
                    size="small"
                    placeholder="Search sessions..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    InputProps={{
                      startAdornment: <Search sx={{ mr: 1, color: 'text.secondary' }} />,
                    }}
                  />
                  <FormControl size="small" sx={{ minWidth: 120 }}>
                    <InputLabel>Status</InputLabel>
                    <Select
                      value={statusFilter}
                      label="Status"
                      onChange={(e) => setStatusFilter(e.target.value)}
                    >
                      <MenuItem value="all">All</MenuItem>
                      <MenuItem value="IN_PROGRESS">In Progress</MenuItem>
                      <MenuItem value="COMPLETED">Completed</MenuItem>
                      <MenuItem value="ABANDONED">Abandoned</MenuItem>
                      <MenuItem value="EXPIRED">Expired</MenuItem>
                    </Select>
                  </FormControl>
                  <IconButton onClick={() => window.location.reload()} disabled={loading}>
                    {loading ? <CircularProgress size={20} /> : <Refresh />}
                  </IconButton>
                </Box>
              </Box>
              {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
              {loading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 200 }}>
                  <CircularProgress />
                </Box>
              ) : (
                <TableContainer component={Paper} sx={{ bgcolor: 'transparent' }}>
                  <Table>
                    <TableHead>
                      <TableRow>
                        <TableCell>
                          <Typography variant="subtitle2" sx={{ fontWeight: 700, fontSize: '0.875rem' }}>
                            Student
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="subtitle2" sx={{ fontWeight: 700, fontSize: '0.875rem' }}>
                            Test
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="subtitle2" sx={{ fontWeight: 700, fontSize: '0.875rem' }}>
                            Status
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="subtitle2" sx={{ fontWeight: 700, fontSize: '0.875rem' }}>
                            Progress
                          </Typography>
                        </TableCell>

                        <TableCell>
                          <Typography variant="subtitle2" sx={{ fontWeight: 700, fontSize: '0.875rem' }}>
                            Started On
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="subtitle2" sx={{ fontWeight: 700, fontSize: '0.875rem' }}>
                            Last Activity
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="subtitle2" sx={{ fontWeight: 700, fontSize: '0.875rem' }}>
                            Actions
                          </Typography>
                        </TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {filteredSessions.map((session) => (
                        <TableRow key={session.id} hover>
                          <TableCell>
                            <Typography variant="body2" sx={{ fontWeight: 600, fontSize: '0.875rem' }}>
                              {session.studentName}
                            </Typography>
                            <Typography variant="body2" color="textSecondary" sx={{ fontSize: '0.75rem' }}>
                              {session.studentPhone}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2" sx={{ fontWeight: 600, fontSize: '0.875rem' }}>
                              {session.subject}, {session.grade}
                            </Typography>
                            <Typography variant="body2" color="textSecondary" sx={{ fontSize: '0.75rem' }}>
                              {session.series}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Chip
                              icon={getStatusIcon(session.status)}
                              label={session.status.replace('_', ' ')}
                              color={getStatusColor(session.status) as any}
                              size="small"
                              sx={{
                                fontSize: '0.75rem',
                                height: '24px',
                                '& .MuiChip-label': {
                                  fontSize: '0.75rem',
                                  fontWeight: 500
                                }
                              }}
                            />
                          </TableCell>
                          <TableCell>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <LinearProgress
                                variant="determinate"
                                value={session.totalQuestions ? (session.answeredQuestions / session.totalQuestions) * 100 : 0}
                                sx={{ flexGrow: 1, height: 8, borderRadius: 4 }}
                              />
                              <Typography variant="body2" sx={{ fontSize: '0.8rem', fontWeight: 500, minWidth: '40px' }}>
                                {session.answeredQuestions}/{session.totalQuestions}
                              </Typography>
                            </Box>
                          </TableCell>

                          <TableCell>
                            <Typography variant="body2" sx={{ fontSize: '0.8rem', fontWeight: 500 }}>
                              {session.startTime ? formatDate(session.startTime) : '-'}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2" sx={{ fontSize: '0.8rem', fontWeight: 500 }}>
                              {formatDate(session.lastActivityTime)}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Box sx={{ display: 'flex', gap: 1 }}>
                              <Tooltip title="View Details">
                                <IconButton
                                  size="small"
                                  onClick={() => handleSessionClick(session)}
                                >
                                  <Visibility />
                                </IconButton>
                              </Tooltip>
                              <Tooltip title="Reset Test">
                                <IconButton
                                  size="small"
                                  onClick={() => handleResetClick(session)}
                                  color="warning"
                                  disabled={isResetting}
                                >
                                  <RestartAlt />
                                </IconButton>
                              </Tooltip>
                            </Box>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </Box>
          </TabPanel>

          <TabPanel value={tabValue} index={1}>
            {/* Performance Analytics Tab */}
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'repeat(2, 1fr)' }, gap: 3 }}>
              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    Performance Trends
                  </Typography>
                  <Box sx={{ height: 300, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <Typography color="textSecondary">
                      Performance charts will be displayed here
                    </Typography>
                  </Box>
                </CardContent>
              </Card>
              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    Subject Performance
                  </Typography>
                  {performance?.subjects.map((subject) => (
                    <Box key={subject} sx={{ mb: 2 }}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                        <Typography variant="body2">{subject}</Typography>
                        <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                          {Math.floor(Math.random() * 30) + 70}%
                        </Typography>
                      </Box>
                      <LinearProgress
                        variant="determinate"
                        value={Math.floor(Math.random() * 30) + 70}
                        sx={{ height: 8, borderRadius: 4 }}
                      />
                    </Box>
                  ))}
                </CardContent>
              </Card>
            </Box>
          </TabPanel>

          <TabPanel value={tabValue} index={3}>
            {/* Live review scheduling (#07). Renders its own locked state when the
                school has no live entitlement, so it is safe to mount for everyone. */}
            <LiveScheduler />
          </TabPanel>

          <TabPanel value={tabValue} index={2}>
            {/* Subject Overview Tab */}
            <Typography variant="h6" gutterBottom>
              Subject-wise Learning Overview
            </Typography>
            <Typography color="textSecondary">
              Detailed subject analysis and recommendations will be displayed here
            </Typography>
          </TabPanel>
        </Card>
      </Container>

      {/* Session Details Dialog */}
      <Dialog
        open={sessionDetailsOpen}
        onClose={() => setSessionDetailsOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          Session Details
          {selectedSession && (
            <Typography variant="subtitle2" color="textSecondary">
              {selectedSession.packageName}
            </Typography>
          )}
        </DialogTitle>
        <DialogContent>
          {selectedSession && (
            <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 2 }}>
              <Box>
                <Typography variant="body2" color="textSecondary">Student Name</Typography>
                <Typography variant="body1">{selectedSession.studentName}</Typography>
              </Box>
              <Box>
                <Typography variant="body2" color="textSecondary">Student Phone</Typography>
                <Typography variant="body1">{selectedSession.studentPhone}</Typography>
              </Box>
              <Box>
                <Typography variant="body2" color="textSecondary">Subject</Typography>
                <Typography variant="body1">{selectedSession.subject}</Typography>
              </Box>
              <Box>
                <Typography variant="body2" color="textSecondary">Grade</Typography>
                <Typography variant="body1">{selectedSession.grade}</Typography>
              </Box>
              <Box>
                <Typography variant="body2" color="textSecondary">Status</Typography>
                <Chip
                  icon={getStatusIcon(selectedSession.status)}
                  label={selectedSession.status.replace('_', ' ')}
                  color={getStatusColor(selectedSession.status) as any}
                  size="small"
                />
              </Box>

              <Box>
                <Typography variant="body2" color="textSecondary">Start Time</Typography>
                <Typography variant="body1">{formatDate(selectedSession.startTime)}</Typography>
              </Box>
              <Box>
                <Typography variant="body2" color="textSecondary">Last Activity</Typography>
                <Typography variant="body1">{formatDate(selectedSession.lastActivityTime)}</Typography>
              </Box>
              {selectedSession.endTime && (
                <Box>
                  <Typography variant="body2" color="textSecondary">End Time</Typography>
                  <Typography variant="body1">{formatDate(selectedSession.endTime)}</Typography>
                </Box>
              )}
              <Box>
                <Typography variant="body2" color="textSecondary">Progress</Typography>
                <Typography variant="body1">
                  {selectedSession.answeredQuestions}/{selectedSession.totalQuestions} questions
                </Typography>
              </Box>
            </Box>
          )}

          {/* Integrity report for this sitting (#06). Mounted inside the detail view
              because a report is about one sitting, and this is where a teacher is
              already looking at one. Renders its own locked/forbidden states. */}
          {selectedSession && (
            <Box sx={{ mt: 3 }}>
              <IntegrityPanel
                sessionId={selectedSession.id}
                studentName={selectedSession.studentName}
              />
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSessionDetailsOpen(false)}>Close</Button>
          <Button variant="contained" startIcon={<Analytics />}>
            View Analytics
          </Button>
        </DialogActions>
      </Dialog>

      {/* Reset Confirmation Dialog */}
      <Dialog
        open={resetConfirmOpen}
        onClose={handleResetCancel}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Warning color="warning" />
            <Typography variant="h6">Reset Test Confirmation</Typography>
          </Box>
        </DialogTitle>
        <DialogContent>
          <Typography variant="body1" gutterBottom>
            Are you sure you want to reset this test session?
          </Typography>
          {sessionToReset && (
            <Box sx={{ mt: 2, p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                {sessionToReset.subject}, {sessionToReset.grade}
              </Typography>
              <Typography variant="body2" color="textSecondary">
                Student: {sessionToReset.studentName}
              </Typography>
              <Typography variant="body2" color="textSecondary">
                Series: {sessionToReset.series}
              </Typography>
            </Box>
          )}
          <Typography variant="body2" color="error" sx={{ mt: 2 }}>
            <strong>Warning:</strong> This action will:
          </Typography>
          <Box component="ul" sx={{ mt: 1, pl: 2 }}>
            <Typography component="li" variant="body2" color="error">
              Delete all previous answers and activities
            </Typography>
            <Typography component="li" variant="body2" color="error">
              Reset the session to the beginning
            </Typography>
            <Typography component="li" variant="body2" color="error">
              Allow the student to retake the test from scratch
            </Typography>
          </Box>
          <Typography variant="body2" sx={{ mt: 2, fontStyle: 'italic' }}>
            This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleResetCancel} disabled={isResetting}>
            Cancel
          </Button>
          <Button
            onClick={handleResetConfirm}
            variant="contained"
            color="warning"
            disabled={isResetting}
            startIcon={isResetting ? <CircularProgress size={16} /> : <RestartAlt />}
          >
            {isResetting ? 'Resetting...' : 'Reset Test'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default WelcomePage;